import React, { useState, useEffect } from 'react';

/**
 * NumberInput - A modern number input component to replace react-number-format
 * 
 * @param {Object} props - Component props
 * @param {number|string} props.value - Input value
 * @param {Function} props.onValueChange - Callback when value changes, receives object with value and formatted values
 * @param {Function} props.onChange - Optional standard input onChange handler 
 * @param {string} props.thousandSeparator - Character for thousand separation (default: ',')
 * @param {string} props.decimalSeparator - Character for decimal separation (default: '.')
 * @param {number} props.decimalScale - Number of decimal places to show (default: 0)
 * @param {string} props.prefix - Text to show before the number
 * @param {string} props.suffix - Text to show after the number
 * @param {boolean} props.allowNegative - Allow negative values (default: false)
 * @param {Function} props.isAllowed - Function to determine if value is allowed
 * @param {boolean} props.allowLeadingZeros - Allow leading zeros (default: false)
 * @param {boolean} props.fixedDecimalScale - Always show specified decimal places (default: false)
 */
export const NumberInput = ({
  value,
  onValueChange,
  onChange,
  thousandSeparator = ',',
  decimalSeparator = '.',
  decimalScale = 0,
  prefix = '',
  suffix = '',
  allowNegative = false,
  isAllowed,
  allowLeadingZeros = false,
  fixedDecimalScale = false,
  onFocus,
  onBlur,
  disabled,
  ...otherProps
}) => {
  // Store raw numeric value internally
  const [rawValue, setRawValue] = useState('');
  
  // Format number with proper separators and scale
  const formatValue = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    
    // Handle negative values
    const isNegative = allowNegative && String(val).startsWith('-');
    let numericString = String(val).replace(/[^\d.]/g, '');

    // Handle decimal places
    const parts = numericString.split('.');
    let integerPart = parts[0];
    let decimalPart = parts.length > 1 ? parts[1] : '';
    
    // Apply decimal scale
    if (decimalPart && decimalScale >= 0) {
      decimalPart = decimalPart.slice(0, decimalScale);
    }
    
    // Remove leading zeros unless allowed
    if (!allowLeadingZeros && integerPart.length > 1) {
      integerPart = integerPart.replace(/^0+/, '');
    }
    if (integerPart === '') integerPart = '0';
    
    // Add thousand separators to integer part
    if (thousandSeparator) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    }
    
    // Construct the final formatted value
    let formattedValue = integerPart;
    
    if (decimalPart || (decimalScale > 0 && fixedDecimalScale) || (parts.length > 1 && decimalScale > 0)) {
      formattedValue += decimalSeparator;
      
      if (fixedDecimalScale) {
        formattedValue += decimalPart.padEnd(decimalScale, '0');
      } else {
        formattedValue += decimalPart;
      }
    }
    
    // Add prefix and suffix
    formattedValue = `${prefix}${formattedValue}${suffix}`;
    
    // Apply negative sign if needed
    if (isNegative) {
      formattedValue = `-${formattedValue}`;
    }
    
    return formattedValue;
  };
  
  // Convert formatted value back to numeric value
  const parseValue = (val) => {
    if (!val) return '';
    
    // Remove prefix and suffix
    let parsedValue = val;
    if (prefix) parsedValue = parsedValue.replace(prefix, '');
    if (suffix) parsedValue = parsedValue.replace(suffix, '');
    
    // Remove thousand separators
    if (thousandSeparator) {
      parsedValue = parsedValue.replaceAll(thousandSeparator, '');
    }
    
    // Ensure decimal separator is a period for parseFloat
    if (decimalSeparator !== '.') {
      parsedValue = parsedValue.replaceAll(decimalSeparator, '.');
    }
    
    return parsedValue;
  };
  
  // Handle input change
  const handleChange = (e) => {
    const inputValue = e.target.value;
    const parsedValue = parseValue(inputValue);
    
    // Check if value is allowed
    if (isAllowed && !isAllowed({ value: parseFloat(parsedValue) || 0 })) {
      return;
    }
    
    // Update internal state
    setRawValue(parsedValue);
    
    // Call parent's callback
    if (onValueChange) {
      const numericValue = parsedValue === '' ? '' : 
        (decimalScale > 0 ? parseFloat(parsedValue) : parseInt(parsedValue, 10));
      
      onValueChange({
        value: numericValue,
        formattedValue: formatValue(parsedValue),
        floatValue: parseFloat(parsedValue) || 0
      });
    }
    
    // Call standard onChange if provided
    if (onChange) {
      onChange(e);
    }
  };
  
  // Format display value on blur
  const handleBlur = (e) => {
    const formattedValue = formatValue(rawValue);
    e.target.value = formattedValue;
    
    if (onBlur) {
      onBlur(e);
    }
  };
  
  // Handle focus event
  const handleFocus = (e) => {
    if (onFocus) {
      onFocus(e);
    }
  };
  
  // Set initial value from props
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setRawValue(String(value));
    }
  }, []);
  
  return (
    <input
      type="text"
      inputMode="decimal"
      value={formatValue(rawValue)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      {...otherProps}
    />
  );
};

// Create named export for backward compatibility
export const NumberFormat = NumberInput;

// Default export
export default NumberInput;